import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '../ui/Button'
import type { RfidEpcScheme, RfidEpcSegment } from '../../types/rfid'
import { sumSegmentLengths, validateScheme } from '../../utils/epcComposer'

interface RfidSchemeEditorProps {
  scheme: RfidEpcScheme
  canEdit: boolean
  isSaving: boolean
  onSave: (input: {
    version_code: string
    constant_code: string
    serial_length: number
    total_length: number
    segments: RfidEpcSegment[]
  }) => Promise<void>
}

export function RfidSchemeEditor({
  scheme,
  canEdit,
  isSaving,
  onSave,
}: RfidSchemeEditorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [versionCode, setVersionCode] = useState(scheme.version_code)
  const [constantCode, setConstantCode] = useState(scheme.constant_code)
  const [serialLength, setSerialLength] = useState(scheme.serial_length)
  const [totalLength, setTotalLength] = useState(scheme.total_length)
  const [segments, setSegments] = useState<RfidEpcSegment[]>(scheme.segments)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setVersionCode(scheme.version_code)
    setConstantCode(scheme.constant_code)
    setSerialLength(scheme.serial_length)
    setTotalLength(scheme.total_length)
    setSegments(scheme.segments.map((segment) => ({ ...segment })))
    setError(null)
  }, [scheme])

  const baseLength = useMemo(() => sumSegmentLengths(segments), [segments])
  const validation = useMemo(
    () =>
      validateScheme({
        segments,
        serialLength,
        totalLength,
        versionCode,
        constantCode,
      }),
    [segments, serialLength, totalLength, versionCode, constantCode]
  )

  function handleSegmentLengthChange(index: number, value: string) {
    const length = Number(value)
    setSegments((prev) =>
      prev.map((segment, i) =>
        i === index ? { ...segment, length: Number.isFinite(length) ? length : 0 } : segment
      )
    )
  }

  async function handleSave() {
    if (!validation.isValid) {
      setError(validation.errors[0] || 'Invalid scheme')
      return
    }
    setError(null)
    try {
      await onSave({
        version_code: versionCode.trim(),
        constant_code: constantCode.trim(),
        serial_length: serialLength,
        total_length: totalLength,
        segments,
      })
      setIsOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save scheme')
    }
  }

  if (!isOpen) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 h-full flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            EPC Scheme
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {scheme.name} · base {sumSegmentLengths(scheme.segments)} + serial {scheme.serial_length} = {scheme.total_length}
          </p>
          <p className="text-sm font-mono text-gray-700 dark:text-gray-300 mt-2">
            version {scheme.version_code} · constant {scheme.constant_code || '(empty)'}
          </p>
        </div>
        {canEdit && (
          <Button variant="secondary" size="sm" onClick={() => setIsOpen(true)}>
            Edit scheme
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-4 h-full">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Edit EPC Scheme
        </h3>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
        >
          Close
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="block text-gray-600 dark:text-gray-300 mb-1">Version code</span>
          <input
            value={versionCode}
            onChange={(e) => setVersionCode(e.target.value)}
            className="w-full h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 font-mono text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 dark:text-gray-300 mb-1">Constant code</span>
          <input
            value={constantCode}
            onChange={(e) => setConstantCode(e.target.value)}
            className="w-full h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 font-mono text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 dark:text-gray-300 mb-1">Serial length</span>
          <input
            type="number"
            min={1}
            value={serialLength}
            onChange={(e) => setSerialLength(Number(e.target.value) || 0)}
            className="w-full h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 dark:text-gray-300 mb-1">Total length</span>
          <input
            type="number"
            min={1}
            value={totalLength}
            onChange={(e) => setTotalLength(Number(e.target.value) || 0)}
            className="w-full h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm"
          />
        </label>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Segment</th>
              <th className="py-2 pr-3">Source</th>
              <th className="py-2">Length</th>
            </tr>
          </thead>
          <tbody>
            {segments.map((segment, index) => (
              <tr key={segment.key} className="border-t border-gray-100 dark:border-gray-700">
                <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">
                  {segment.label}
                </td>
                <td className="py-2 pr-3 font-mono text-xs text-gray-500">{segment.source}</td>
                <td className="py-2">
                  <input
                    type="number"
                    min={1}
                    value={segment.length}
                    onChange={(e) => handleSegmentLengthChange(index, e.target.value)}
                    className="w-20 h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className={`text-sm font-medium ${
          validation.isValid
            ? 'text-teal-700 dark:text-teal-300'
            : 'text-amber-700 dark:text-amber-300'
        }`}
      >
        Base {baseLength} + serial {serialLength} = {baseLength + serialLength}
        {' / '}
        required {totalLength}
      </div>

      {error && (
        <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={() => setIsOpen(false)} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!validation.isValid || isSaving}>
          {isSaving ? 'Saving…' : 'Save scheme'}
        </Button>
      </div>
    </div>
  )
}
